import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

// Reusable premium 9:16 video section. Renders nothing until a valid videoUrl
// is provided (no broken/empty player). Autoplays muted, audio after tap.
export default function VideoSection({ videoUrl, poster, eyebrow, title, text, cta, id = "video" }) {
  const wrapRef = useRef(null);
  const vref = useRef(null);
  const [inView, setInView] = useState(false);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (!videoUrl) return undefined;
    const el = wrapRef.current;
    if (!el) return undefined;
    const ob = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); ob.disconnect(); } },
      { rootMargin: "200px" }
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [videoUrl]);

  useEffect(() => {
    const v = vref.current;
    if (v && inView) v.play().catch(() => {});
  }, [inView]);

  if (!videoUrl) return null;

  const toggleMute = () => {
    const v = vref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };

  return (
    <section id={id} className="cm-reveal py-24 lg:py-32">
      <div className="mx-auto max-w-[1360px] px-6 lg:px-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[1fr_0.8fr] lg:gap-20">
          <div>
            {eyebrow && <p className="label-mono mb-6">{eyebrow}</p>}
            {title && (
              <h2 className="h-display text-[clamp(2rem,4.2vw,3.2rem)]">{title}</h2>
            )}
            {text && (
              <p className="mt-6 max-w-[460px] text-[1.02rem] leading-relaxed text-[#5b5b54]">
                {text}
              </p>
            )}
            {cta}
          </div>

          <div ref={wrapRef} className="mx-auto w-full max-w-[340px]">
            <div className="relative overflow-hidden rounded-[22px] border border-[var(--cm-line)] bg-black shadow-[0_40px_90px_-40px_rgba(0,0,0,0.5)]">
              <div className="aspect-[9/16]">
                <video
                  ref={vref}
                  className="h-full w-full object-cover"
                  poster={poster || undefined}
                  src={inView ? videoUrl : undefined}
                  muted={muted}
                  loop
                  playsInline
                  preload="none"
                  data-testid="video-section-player"
                />
              </div>
              <button
                type="button"
                onClick={toggleMute}
                aria-label={muted ? "Attiva audio" : "Disattiva audio"}
                data-testid="video-section-mute"
                className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[var(--cm-ink)] backdrop-blur transition-transform hover:scale-105"
              >
                {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
